import { denormalize, normalize } from 'normalizr'
import { useSelector } from 'react-redux'
import get from 'lodash/get'
import api from '../../api'
import { inOfficebillingOrdersSchema } from '../normalizr'
import { getPaginationHeaders, useAsyncAction } from '../utils'
import createSlice from '../create-slice'
import { getEntities } from './entities'

const billingOrdersInitialState = {
  officeBillingOrders: {},
  cptCodes: [],
}

const getStoreKey = (checkDate, query) => `${checkDate}:${query || ''}`

const inOfficeBillingOrdersSlice = createSlice({
  name: 'inOfficebillingOrders',
  initialState: billingOrdersInitialState,
  reducers: {
    setBillingOrders: (
      state,
      { payload: { checkDate, query, result, pageSize, total } },
    ) => {
      const key = getStoreKey(checkDate, query)
      state.officeBillingOrders[key] = state.officeBillingOrders[key] || {}
      state.officeBillingOrders[key].ids = result
      state.officeBillingOrders[key].pageSize = pageSize
      state.officeBillingOrders[key].total = total
    },
    setCPTCodes: (state, { payload }) => {
      const existingIndex = state.cptCodes.findIndex(
        entry =>
          payload.practiceId === entry.practiceId &&
          payload.cycleId === entry.cycleId &&
          payload.from === entry.from &&
          payload.to === entry.to,
      )

      if (existingIndex >= 0) {
        state.cptCodes[existingIndex] = {
          ...state.cptCodes[existingIndex],
          ...payload,
        }
      } else {
        state.cptCodes.push(payload)
      }
    },
  },
})

const { actions } = inOfficeBillingOrdersSlice

const selectState = state => state[inOfficeBillingOrdersSlice.name]

export function useBillingOrders(checkDate, query) {
  const { officeBillingOrders } = useSelector(selectState)
  const entities = useSelector(getEntities)
  const key = getStoreKey(checkDate, query)
  return denormalize(
    get(officeBillingOrders, [key, 'ids'], []),
    [inOfficebillingOrdersSchema],
    entities,
  )
}

export function useBillingOrdersPagination(checkDate, query) {
  const { officeBillingOrders } = useSelector(selectState)
  const key = getStoreKey(checkDate, query)
  const { pageSize, total } = get(officeBillingOrders, [key], {})
  return { pageSize, total }
}

function fetchBillingOrders(practiceId, checkDate, page, query) {
  return async dispatch => {
    const { body, headers } = await api.inOfficeBillingOrders.list(
      practiceId,
      checkDate,
      page,
      query,
    )
    dispatch(
      actions.setBillingOrders({
        checkDate,
        query,
        ...normalize(body, [inOfficebillingOrdersSchema]),
        ...getPaginationHeaders(headers),
      }),
    )
  }
}

export function useFetchBillingOrders(practiceId, checkDate, page, query) {
  return useAsyncAction(fetchBillingOrders, practiceId, checkDate, page, query)
}

export function markBillingOrderAsBilled(id, billed) {
  const type = 'MARK_AS_BILLED' // Just to update entities slice
  return async dispatch => {
    const body = await api.billingOrders.markAsBilled(id, { billed })
    return dispatch({
      type,
      payload: normalize(body, inOfficebillingOrdersSchema),
    })
  }
}

export function useCPTCodes() {
  return useSelector(selectState).cptCodes
}

export function fetchCPTCodes(practiceId, cycleId) {
  return async dispatch => {
    const { body } = await api.inOfficeBillingOrders.listCPTCodes(
      practiceId,
      cycleId,
    )
    dispatch(
      actions.setCPTCodes({
        practiceId,
        cycleId,
        data: Object.keys(body).map(key => ({
          id: key,
          cptCode: key,
          amount: body[key],
        })),
      }),
    )
  }
}

export function useFetchCPTCodes(practiceId, cycleId) {
  return useAsyncAction(fetchCPTCodes, practiceId, cycleId)
}

export default inOfficeBillingOrdersSlice
